import type { Client } from '@urql/core'
import { graphql } from 'app/generated/gql'

import type { Location as LocationType, LocationListResult } from 'app/generated/graphql'
import type { SchedulingRoomsRead } from 'app/types/appointment-calendar'
import { EntityStore } from './EntityStore'

const LIST_QUERY = graphql(`
  query LocationStoreLocations($search: String, $pagination: PaginationInput, $sort: SortInput) {
    locations(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        version
        createdDate
        updatedDate
        id
        orgId
        workspaceId
        name
        pms
        pmsIntegratedDate
        migrationDate
        sikkaOfficeId
        sikkaPracticeId
        kollaConnectorId
        kollaConsumerId
        kollaPracticeId
        kollaPageSize
        availabilityMode
        callRailCompanyId
        elevenLabsAgentId
        callRailBackfillStartDate
        jotformFormIds
        facebookFormIds
        carestackId
        ringCentralPhoneNumbers
        twilioNumbers {
          id
          type
          phone
          sourceName
          label
          poolId
          areaCode
          poolSize
          sessionDuration
        }
        primaryNumberId
        officeTransferNumberId
        googleAdsCustomerId
        facebookAdSetIds
        city
        state
        address
        zip
        phone
        description
        addressLine2
        timezone
        openHours {
          sunday {
            hours {
              from
              to
            }
            open
          }
          monday {
            hours {
              from
              to
            }
            open
          }
          tuesday {
            hours {
              from
              to
            }
            open
          }
          wednesday {
            hours {
              from
              to
            }
            open
          }
          thursday {
            hours {
              from
              to
            }
            open
          }
          friday {
            hours {
              from
              to
            }
            open
          }
          saturday {
            hours {
              from
              to
            }
            open
          }
        }
        tags
        orgTags
        labels
        products
        type
        callRailRouting {
          tag
          locationId
        }
        primaryTrackingNumber
        primarySmsNumber
        businessPhone
        officeMainNumber
        officeTransferNumber
        jotFormIgnoredForms
        lastPmsSyncDate
        lastSyncFailureDate
        lastPmsSyncStartDate
        lastPmsSyncCompleteDate
        currentStatus
        notes {
          text
          date
          userId
        }
        lastRingCentralSyncDate
        lastCallrailWebhookDate
        lastTwilioWebhookDate
        lastAICallDate
        lastSikkaWebhookDate
        lastJotformWebhookDate
        lastFacebookWebhookDate
        facebookSpendUpdateDate
        googleSpendUpdateDate
        facebookAdsActive
        googleAdsActive
        isSchedulerLive
        isAIAgentLive
        allowDoubleBookingScheduling
        gravityFormsKey
        webflowFormsKey
        googleAdsV2 {
          accountId
          campaignId
          adGroupId
        }
        googleAdsV3 {
          accountId
          campaignIds
          conversionActions
        }
        facebookAdsV2 {
          accountId
          campaignId
          adSetId
        }
        facebookLeadsPages {
          id
          formIds
        }
        jotform {
          folderId
          formIds
        }
        jotformV2 {
          labelId
          formIds
        }
        settings {
          newPatientCalculation
          appointmentsSetNewPatient
          appointmentCompletionStatuses
          transactionBrokenCodes
          newPatientCodes
          ignorePII
          excludeParentsFromNewPatients
          firstVisitSetNewPatient
        }
        archived
        lastGravityFormWebhookDate
        lastPatientSyncDate
        lastAppointmentSyncDate
        lastTransactionSyncDate
        lastProcedureSyncDate
        lastTreatmentPlanSyncDate
        lastPatientReceivedDate
        lastAppointmentReceivedDate
        lastTransactionReceivedDate
        lastProcedureReceivedDate
        lastTreatmentPlanReceivedDate
        ascendLocationId
        alarmQuietStart
        alarmQuietEnd
        dailySyncHour
        pmsIntegrated
      }
      total
      page
    }
  }
`)

const GET_QUERY = graphql(`
  query LocationStoreLocation($id: ID!) {
    location(id: $id) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      name
      pms
      pmsIntegratedDate
      migrationDate
      sikkaOfficeId
      sikkaPracticeId
      kollaConnectorId
      kollaConsumerId
      kollaPracticeId
      kollaPageSize
      availabilityMode
      callRailCompanyId
      elevenLabsAgentId
      callRailBackfillStartDate
      jotformFormIds
      facebookFormIds
      carestackId
      ringCentralPhoneNumbers
      twilioNumbers {
        id
        type
        phone
        sourceName
        label
        poolId
        areaCode
        poolSize
        sessionDuration
      }
      primaryNumberId
      officeTransferNumberId
      googleAdsCustomerId
      facebookAdSetIds
      city
      state
      address
      zip
      phone
      description
      addressLine2
      timezone
      openHours {
        sunday {
          hours {
            from
            to
          }
          open
        }
        monday {
          hours {
            from
            to
          }
          open
        }
        tuesday {
          hours {
            from
            to
          }
          open
        }
        wednesday {
          hours {
            from
            to
          }
          open
        }
        thursday {
          hours {
            from
            to
          }
          open
        }
        friday {
          hours {
            from
            to
          }
          open
        }
        saturday {
          hours {
            from
            to
          }
          open
        }
      }
      tags
      orgTags
      labels
      products
      type
      callRailRouting {
        tag
        locationId
      }
      primaryTrackingNumber
      primarySmsNumber
      businessPhone
      officeMainNumber
      officeTransferNumber
      jotFormIgnoredForms
      lastPmsSyncDate
      lastSyncFailureDate
      lastPmsSyncStartDate
      lastPmsSyncCompleteDate
      currentStatus
      notes {
        text
        date
        userId
      }
      lastRingCentralSyncDate
      lastCallrailWebhookDate
      lastTwilioWebhookDate
      lastAICallDate
      lastSikkaWebhookDate
      lastJotformWebhookDate
      lastFacebookWebhookDate
      facebookSpendUpdateDate
      googleSpendUpdateDate
      facebookAdsActive
      googleAdsActive
      isSchedulerLive
      isAIAgentLive
      allowDoubleBookingScheduling
      gravityFormsKey
      webflowFormsKey
      googleAdsV2 {
        accountId
        campaignId
        adGroupId
      }
      googleAdsV3 {
        accountId
        campaignIds
        conversionActions
      }
      facebookAdsV2 {
        accountId
        campaignId
        adSetId
      }
      facebookLeadsPages {
        id
        formIds
      }
      jotform {
        folderId
        formIds
      }
      jotformV2 {
        labelId
        formIds
      }
      settings {
        newPatientCalculation
        appointmentsSetNewPatient
        appointmentCompletionStatuses
        transactionBrokenCodes
        newPatientCodes
        ignorePII
        excludeParentsFromNewPatients
        firstVisitSetNewPatient
      }
      archived
      lastGravityFormWebhookDate
      lastPatientSyncDate
      lastAppointmentSyncDate
      lastTransactionSyncDate
      lastProcedureSyncDate
      lastTreatmentPlanSyncDate
      lastPatientReceivedDate
      lastAppointmentReceivedDate
      lastTransactionReceivedDate
      lastProcedureReceivedDate
      lastTreatmentPlanReceivedDate
      ascendLocationId
      alarmQuietStart
      alarmQuietEnd
      dailySyncHour
      pmsIntegrated
    }
  }
`)

const CREATE_MUTATION = graphql(`
  mutation LocationStoreCreate($input: CreateLocationInput!) {
    createLocation(input: $input) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      name
      pms
      pmsIntegratedDate
      migrationDate
      sikkaOfficeId
      sikkaPracticeId
      kollaConnectorId
      kollaConsumerId
      kollaPracticeId
      kollaPageSize
      availabilityMode
      callRailCompanyId
      elevenLabsAgentId
      callRailBackfillStartDate
      jotformFormIds
      facebookFormIds
      carestackId
      ringCentralPhoneNumbers
      twilioNumbers {
        id
        type
        phone
        sourceName
        label
        poolId
        areaCode
        poolSize
        sessionDuration
      }
      primaryNumberId
      officeTransferNumberId
      googleAdsCustomerId
      facebookAdSetIds
      city
      state
      address
      zip
      phone
      description
      addressLine2
      timezone
      openHours {
        sunday {
          hours {
            from
            to
          }
          open
        }
        monday {
          hours {
            from
            to
          }
          open
        }
        tuesday {
          hours {
            from
            to
          }
          open
        }
        wednesday {
          hours {
            from
            to
          }
          open
        }
        thursday {
          hours {
            from
            to
          }
          open
        }
        friday {
          hours {
            from
            to
          }
          open
        }
        saturday {
          hours {
            from
            to
          }
          open
        }
      }
      tags
      orgTags
      labels
      products
      type
      callRailRouting {
        tag
        locationId
      }
      primaryTrackingNumber
      primarySmsNumber
      businessPhone
      officeMainNumber
      officeTransferNumber
      jotFormIgnoredForms
      lastPmsSyncDate
      lastSyncFailureDate
      lastPmsSyncStartDate
      lastPmsSyncCompleteDate
      currentStatus
      notes {
        text
        date
        userId
      }
      lastRingCentralSyncDate
      lastCallrailWebhookDate
      lastTwilioWebhookDate
      lastAICallDate
      lastSikkaWebhookDate
      lastJotformWebhookDate
      lastFacebookWebhookDate
      facebookSpendUpdateDate
      googleSpendUpdateDate
      facebookAdsActive
      googleAdsActive
      isSchedulerLive
      isAIAgentLive
      allowDoubleBookingScheduling
      gravityFormsKey
      webflowFormsKey
      googleAdsV2 {
        accountId
        campaignId
        adGroupId
      }
      googleAdsV3 {
        accountId
        campaignIds
        conversionActions
      }
      facebookAdsV2 {
        accountId
        campaignId
        adSetId
      }
      facebookLeadsPages {
        id
        formIds
      }
      jotform {
        folderId
        formIds
      }
      jotformV2 {
        labelId
        formIds
      }
      settings {
        newPatientCalculation
        appointmentsSetNewPatient
        appointmentCompletionStatuses
        transactionBrokenCodes
        newPatientCodes
        ignorePII
        excludeParentsFromNewPatients
        firstVisitSetNewPatient
      }
      archived
      lastGravityFormWebhookDate
      lastPatientSyncDate
      lastAppointmentSyncDate
      lastTransactionSyncDate
      lastProcedureSyncDate
      lastTreatmentPlanSyncDate
      lastPatientReceivedDate
      lastAppointmentReceivedDate
      lastTransactionReceivedDate
      lastProcedureReceivedDate
      lastTreatmentPlanReceivedDate
      ascendLocationId
      alarmQuietStart
      alarmQuietEnd
      dailySyncHour
      pmsIntegrated
    }
  }
`)

const UPDATE_MUTATION = graphql(`
  mutation LocationStoreUpdate($id: ID!, $input: UpdateLocationInput!) {
    updateLocation(id: $id, input: $input) {
      version
      createdDate
      updatedDate
      id
      orgId
      workspaceId
      name
      pms
      pmsIntegratedDate
      migrationDate
      sikkaOfficeId
      sikkaPracticeId
      kollaConnectorId
      kollaConsumerId
      kollaPracticeId
      kollaPageSize
      availabilityMode
      callRailCompanyId
      elevenLabsAgentId
      callRailBackfillStartDate
      jotformFormIds
      facebookFormIds
      carestackId
      ringCentralPhoneNumbers
      twilioNumbers {
        id
        type
        phone
        sourceName
        label
        poolId
        areaCode
        poolSize
        sessionDuration
      }
      primaryNumberId
      officeTransferNumberId
      googleAdsCustomerId
      facebookAdSetIds
      city
      state
      address
      zip
      phone
      description
      addressLine2
      timezone
      openHours {
        sunday {
          hours {
            from
            to
          }
          open
        }
        monday {
          hours {
            from
            to
          }
          open
        }
        tuesday {
          hours {
            from
            to
          }
          open
        }
        wednesday {
          hours {
            from
            to
          }
          open
        }
        thursday {
          hours {
            from
            to
          }
          open
        }
        friday {
          hours {
            from
            to
          }
          open
        }
        saturday {
          hours {
            from
            to
          }
          open
        }
      }
      tags
      orgTags
      labels
      products
      type
      callRailRouting {
        tag
        locationId
      }
      primaryTrackingNumber
      primarySmsNumber
      businessPhone
      officeMainNumber
      officeTransferNumber
      jotFormIgnoredForms
      lastPmsSyncDate
      lastSyncFailureDate
      lastPmsSyncStartDate
      lastPmsSyncCompleteDate
      currentStatus
      notes {
        text
        date
        userId
      }
      lastRingCentralSyncDate
      lastCallrailWebhookDate
      lastTwilioWebhookDate
      lastAICallDate
      lastSikkaWebhookDate
      lastJotformWebhookDate
      lastFacebookWebhookDate
      facebookSpendUpdateDate
      googleSpendUpdateDate
      facebookAdsActive
      googleAdsActive
      isSchedulerLive
      isAIAgentLive
      allowDoubleBookingScheduling
      gravityFormsKey
      webflowFormsKey
      googleAdsV2 {
        accountId
        campaignId
        adGroupId
      }
      googleAdsV3 {
        accountId
        campaignIds
        conversionActions
      }
      facebookAdsV2 {
        accountId
        campaignId
        adSetId
      }
      facebookLeadsPages {
        id
        formIds
      }
      jotform {
        folderId
        formIds
      }
      jotformV2 {
        labelId
        formIds
      }
      settings {
        newPatientCalculation
        appointmentsSetNewPatient
        appointmentCompletionStatuses
        transactionBrokenCodes
        newPatientCodes
        ignorePII
        excludeParentsFromNewPatients
        firstVisitSetNewPatient
      }
      archived
      lastGravityFormWebhookDate
      lastPatientSyncDate
      lastAppointmentSyncDate
      lastTransactionSyncDate
      lastProcedureSyncDate
      lastTreatmentPlanSyncDate
      lastPatientReceivedDate
      lastAppointmentReceivedDate
      lastTransactionReceivedDate
      lastProcedureReceivedDate
      lastTreatmentPlanReceivedDate
      ascendLocationId
      alarmQuietStart
      alarmQuietEnd
      dailySyncHour
      pmsIntegrated
    }
  }
`)

const DELETE_MUTATION = graphql(`
  mutation DeleteLocation($id: ID!) {
    deleteLocation(id: $id)
  }
`)

export class LocationStore extends EntityStore<
  LocationType,
  LocationListResult,
  typeof CREATE_MUTATION,
  typeof UPDATE_MUTATION
> {
  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: GET_QUERY,
      createMutation: CREATE_MUTATION,
      updateMutation: UPDATE_MUTATION,
      deleteMutation: DELETE_MUTATION,
      typename: `Location`,
      extractList: (data: unknown) => (data as { locations: LocationListResult }).locations,
      extractOne: (data: unknown) => (data as { location: LocationType | undefined }).location,
      extractMutated: (data: unknown) => {
        const d = data as { createLocation?: LocationType; updateLocation?: LocationType }
        return d.createLocation ?? d.updateLocation
      },
    })
  }

  private roomReads = new Map<string, Promise<SchedulingRoomsRead>>()

  /**
   * A location's rooms for a day and which room each booked visit is in. REST because the GraphQL
   * appointment carries no room; it is the call the Schedule board reads. It takes about two
   * seconds, so each location and day is read once per session, and a failed read is forgotten.
   */
  readRooms(locationId: string, day: string): Promise<SchedulingRoomsRead> {
    const key = `${locationId}|${day}`
    const cached = this.roomReads.get(key)
    if (cached) return cached
    const read = window
      .fetch(`/api/scheduling/locations/${encodeURIComponent(locationId)}/scheduling-data?day=${day}`, {
        credentials: `include`,
      })
      .then(async (response) => {
        if (!response.ok) throw new Error(`scheduling-data ${response.status}`)
        const data = (await response.json()) as Omit<SchedulingRoomsRead, `locationId`>
        return { locationId, operatories: data.operatories ?? [], appointments: data.appointments ?? [] }
      })
    this.roomReads.set(key, read)
    read.catch(() => this.roomReads.delete(key))
    return read
  }
}
